/**
 * Tabbed File Names / Date Range modal, reused for two actions:
 * - `trackStatus` (default): persists status via
 *   {@link runNycbsPharmaStatusTracking} / {@link runNycbsPharmaStatusTrackingByDateRange}.
 * - `deliveryPipeline`: triggers the unified delivery pipeline via
 *   {@link handleRunDeliveryPipeline}, replacing the legacy per-step actions
 *   above on the Pharma STP File tab's Actions menu.
 */
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { controlToastState, Modal } from "risa-oasis-ui_v2";
import {
  runNycbsPharmaStatusTracking,
  runNycbsPharmaStatusTrackingByDateRange,
} from "../../../api/postCall/runNycbsPharmaStatusTracking";
import { FilterValues } from "../../../data-model/filterValues";
import { ModalId } from "../../../enums/modalId";
import { PushToSftpModalModes } from "../../../enums/StringLiterals/pushToSftpModalModes";
import { PushToSftpModalTableColumnKeys } from "../../../enums/tableColumnKeys";
import { handleRunDeliveryPipeline } from "../../../pages/pharmaStpFile/actionHandlers/runDeliveryPipelineHandler";
import { convertDateToAPIFormat } from "../../../pages/pharmaStpFile/utils/convertDateToApiFormat";
import { PharmaStpFileFilterKeys } from "../../../pages/pharmaStpFile/utils/createFilterSections";
import { AppDispatch } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { onTrackStatus } from "../pushToSftpModal/actionHandlers/onTrackStatus";
import PushToSftpTableContent, {
  PushToSftpTableState,
} from "../pushToSftpModal/components/PushToSftpTableContent";
import { useTableDataForSftpPush } from "../pushToSftpModal/hooks/useTableDataForSftpPush";
import AsteraTrackStatusContent, {
  AsteraTrackStatusDateRange,
} from "./asteraTrackStatusContent";

const TAB_IDS = {
  FILE_NAMES: "file-names",
  DATE_RANGE: "date-range",
} as const;

const tabs = [
  { id: TAB_IDS.FILE_NAMES, label: "File Names" },
  { id: TAB_IDS.DATE_RANGE, label: "Date Range" },
];

type TrackStatusAction = "trackStatus" | "deliveryPipeline";

const ACTION_CONFIG: Record<
  TrackStatusAction,
  { dialogId: string; title: string; saveButtonText: string }
> = {
  trackStatus: {
    dialogId: ModalId.ASTERA_TRACK_STATUS_MODAL,
    title: "Track Status",
    saveButtonText: "Run Status",
  },
  deliveryPipeline: {
    dialogId: ModalId.RUN_DELIVERY_PIPELINE_MODAL,
    title: "Run Delivery Pipeline",
    saveButtonText: "Run Pipeline",
  },
};

/** Builds the delivery-pipeline filters for the currently selected tab's scope. */
const buildDeliveryPipelineFilterValues = (
  selectedTab: string,
  tableData: ReturnType<typeof useTableDataForSftpPush>["tableData"],
  dateRangeSelection: AsteraTrackStatusDateRange | null,
): FilterValues[] => {
  if (
    selectedTab === TAB_IDS.DATE_RANGE &&
    dateRangeSelection?.startDate &&
    dateRangeSelection?.endDate
  ) {
    return [
      {
        name: "run-delivery-pipeline-modal",
        filterCount: 1,
        values: {
          [PharmaStpFileFilterKeys.DOW]: {
            values: [
              convertDateToAPIFormat(dateRangeSelection.startDate),
              convertDateToAPIFormat(dateRangeSelection.endDate),
            ],
            type: "date",
          },
        },
      },
    ];
  }

  const selectedFilenames = tableData
    .filter((item) => item[PushToSftpModalTableColumnKeys.SELECT]?.isChecked)
    .map((item) => item[PushToSftpModalTableColumnKeys.FILENAME])
    .filter((filename): filename is string => filename !== undefined);

  return [
    {
      name: "run-delivery-pipeline-modal",
      filterCount: 1,
      values: {
        [PharmaStpFileFilterKeys.BATCH]: {
          values: selectedFilenames,
          type: "string",
        },
      },
    },
  ];
};

interface TrackStatusTabbedModalProps {
  onClose: () => void;
  onSuccess: () => void;
  action?: TrackStatusAction;
}

const TrackStatusTabbedModal = (props: TrackStatusTabbedModalProps) => {
  const { onClose, onSuccess, action = "trackStatus" } = props;
  const config = ACTION_CONFIG[action];
  const dispatch = useDispatch<AppDispatch>();
  const [selectedTab, setSelectedTab] = useState<string>(TAB_IDS.FILE_NAMES);
  const [isSaving, setIsSaving] = useState(false);
  const [dateRangeSelection, setDateRangeSelection] =
    useState<AsteraTrackStatusDateRange | null>(null);
  const abortTrackStatusTimeout = useRef<(() => void) | null>(null);
  const dateRangeCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const tableState = useTableDataForSftpPush(PushToSftpModalModes.TRACK);
  const { tableData } = tableState;

  const runTrackStatusNoAbort = (filenames: string[]) =>
    runNycbsPharmaStatusTracking(filenames);

  useEffect(() => {
    return () => {
      if (abortTrackStatusTimeout.current) {
        abortTrackStatusTimeout.current();
      }
      if (dateRangeCloseTimeout.current) {
        clearTimeout(dateRangeCloseTimeout.current);
      }
    };
  }, []);

  const handleTabChange = (tabId: string) => {
    // Leaving the Date Range tab remounts AsteraTrackStatusContent (its own
    // display resets to blank on the next visit) — clear the selection here
    // too, so a stale, no-longer-visible range can't be silently submitted.
    if (selectedTab === TAB_IDS.DATE_RANGE && tabId !== TAB_IDS.DATE_RANGE) {
      setDateRangeSelection(null);
    }
    setSelectedTab(tabId);
  };

  const handleSaveDeliveryPipeline = async () => {
    setIsSaving(true);
    try {
      const succeeded = await handleRunDeliveryPipeline(
        buildDeliveryPipelineFilterValues(
          selectedTab,
          tableData,
          dateRangeSelection,
        ),
      );
      if (succeeded) {
        onSuccess();
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Runs track status: date-range tab posts a filing-range filter; file-names tab runs
   * `onTrackStatus` with filename filters. Both use NYCBS pharma status tracking endpoints.
   */
  const handleSaveTrackStatus = async () => {
    setIsSaving(true);
    let isDateRangePath = false;
    try {
      if (
        selectedTab === TAB_IDS.DATE_RANGE &&
        dateRangeSelection?.startDate &&
        dateRangeSelection?.endDate
      ) {
        isDateRangePath = true;
        const org_id = getOrgIdForFetchExternalWorklist();
        const payload = {
          org_id,
          filters: {
            date_of_filing: {
              start_date: moment(dateRangeSelection.startDate).format(
                "MM/DD/YYYY",
              ),
              end_date: moment(dateRangeSelection.endDate).format("MM/DD/YYYY"),
            },
          },
        };
        runNycbsPharmaStatusTrackingByDateRange(payload).catch(() => {
          controlToastState("track-status-error");
        });

        dateRangeCloseTimeout.current = setTimeout(() => {
          dateRangeCloseTimeout.current = null;
          controlToastState("track-status-success");
          onClose();
        }, 5000);
      } else {
        const { promise, abort } = onTrackStatus(
          tableData,
          dispatch,
          runTrackStatusNoAbort,
        );
        abortTrackStatusTimeout.current = abort;
        await promise;
        abortTrackStatusTimeout.current = null;
        onSuccess();
      }
    } finally {
      if (!isDateRangePath) {
        setIsSaving(false);
      }
    }
  };

  const handleSave =
    action === "deliveryPipeline"
      ? handleSaveDeliveryPipeline
      : handleSaveTrackStatus;

  const hasSelectedRows = tableData.some(
    (row) =>
      row[PushToSftpModalTableColumnKeys.SELECT]?.isChecked &&
      !row[PushToSftpModalTableColumnKeys.SELECT]?.disabled,
  );

  const hasDateRangeSelection =
    dateRangeSelection?.startDate != null &&
    dateRangeSelection?.endDate != null;

  const canSave =
    (selectedTab === TAB_IDS.FILE_NAMES && hasSelectedRows) ||
    (selectedTab === TAB_IDS.DATE_RANGE && hasDateRangeSelection);

  const renderContent = () => {
    switch (selectedTab) {
      case TAB_IDS.FILE_NAMES:
        return (
          <PushToSftpTableContent
            mode={PushToSftpModalModes.TRACK}
            showDownloadColumn={false}
            tableState={tableState as PushToSftpTableState}
          />
        );
      case TAB_IDS.DATE_RANGE:
        return (
          <AsteraTrackStatusContent
            onDateRangeChange={(hasSelection, dateRange) => {
              setDateRangeSelection(
                hasSelection && dateRange ? dateRange : null,
              );
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      dialogId={config.dialogId}
      title={config.title}
      onClose={() => {
        if (abortTrackStatusTimeout.current) {
          abortTrackStatusTimeout.current();
        }
        if (dateRangeCloseTimeout.current) {
          clearTimeout(dateRangeCloseTimeout.current);
          dateRangeCloseTimeout.current = null;
        }
        onClose();
      }}
      onCancel={onClose}
      onSave={isSaving ? () => {} : handleSave}
      saveButtonText={isSaving ? "Running..." : config.saveButtonText}
      disableSave={isSaving || !canSave}
      cancelText="Cancel"
      heightPercentage={selectedTab === TAB_IDS.DATE_RANGE ? 60 : 70}
      hideFooter={false}
    >
      <div
        className={`flex h-full flex-col ${selectedTab === TAB_IDS.DATE_RANGE ? "track-status-tabbed-modal--date-range-tab" : ""}`}
      >
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tab.id === selectedTab
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="content--container flex h-full flex-1 flex-col overflow-y-auto p-2">
          {renderContent()}
        </div>
      </div>
    </Modal>
  );
};

export default TrackStatusTabbedModal;
